import { useParams, Navigate } from 'react-router-dom';
import StructuredDataDisplay from './StructuredDataDisplay';
import { useEffect, useRef, useState } from 'react';
import { Header, Footer, Stepper, PageCard } from '.';
import { useSummaryData } from '../hooks/useSummaryData';
import ThemeNavigator from './ThemeNavigator';
import SummaryEditor from './SummaryEditor';
import ErrorBoundary from './ErrorBoundary';
import { Case, isCaseLocked } from '../types';
import { getCase } from '../hooks/cases';

const isTestMode = process.env.REACT_APP_USE_TEST_JWT === 'true';

const SummaryPageContent: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const menuRef = useRef<HTMLDivElement>(null);
  const [caseItem, setCaseItem] = useState<Case | null>(null);

  const {
    editor,
    extractResults,
    currentThemeIndex,
    isSaving,
    isCompleting,
    blockOpen,
    setBlockOpen,
    handlePreviousTheme,
    handleNextTheme,
    handleThemeSelect,
    handleCompleteConversation,
    handleSaveSummary,
    showSaveSuccess,
    setShowSaveSuccess,
  } = useSummaryData(caseId, menuRef);

  useEffect(() => {
    if (!caseId) return;
    getCase(caseId).then(setCaseItem).catch(() => setCaseItem(null));
  }, [caseId]);

  // "summary_submitted" means the submission is already on its way to
  // CompetenSYS, so it is just as read-only as "closed".
  const isLocked = isCaseLocked(caseItem?.status);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isLocked);
    }
  }, [editor, isLocked]);

  if (!caseId) {
    return <Navigate to="/" replace />;
  }

  const hasEnqueuedSummaries = extractResults.some(
    (result) => result.workflow_status === "enqueued",
  );
  const allSummariesReady = extractResults.length > 0 && !hasEnqueuedSummaries;
  const isButtonDisabled = isCompleting || !allSummariesReady || isLocked;

  return (
    <div className="min-h-screen bg-brand-primary flex flex-col">
      <Header />

      <PageCard>
        <Stepper
          step={extractResults && extractResults.length > 0 ? (isLocked ? 4 : 3) : 3}
          caseId={caseId}
          isLocked={isLocked}
        />

        {extractResults && extractResults.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E1D6C6] overflow-hidden">
            <ThemeNavigator
              currentThemeIndex={currentThemeIndex}
              extractResults={extractResults}
              onPreviousTheme={handlePreviousTheme}
              onNextTheme={handleNextTheme}
              onThemeSelect={handleThemeSelect}
              readOnly={isLocked}
            />

            <SummaryEditor
              editor={editor}
              menuRef={menuRef}
              blockOpen={blockOpen}
              setBlockOpen={setBlockOpen}
              isSaving={isSaving}
              onSave={handleSaveSummary}
              onSend={handleCompleteConversation}
              isSendDisabled={isButtonDisabled}
              isCompleting={isCompleting}
              hasEnqueuedSummaries={hasEnqueuedSummaries}
              readOnly={isLocked}
            />

            {/* Inspection aid only. Its own copy says users must not see these
                answers: they edit the summary, and the answers are generated
                from it. */}
            {isTestMode && (
              <div className="border-t border-[#E1D6C6]">
                <StructuredDataDisplay extractResults={[extractResults[currentThemeIndex]]} />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <section className="flex-1 lg:basis-[55%] space-y-6">
              <div className="inline-flex items-center gap-3">
                <span className="badge-context">Gespreksverwerking</span>
                <div className="w-7 h-7 flex items-center justify-center">
                  <div className="w-6 h-6 border-3 border-brand-blauw border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold text-brand-red">
                  Resultaat verwerking
                </h1>
                <p className="text-base sm:text-lg text-brand-blauw leading-relaxed mt-2">
                  Hieronder wordt de uitwerking vanuit het gesprek getoond. De onderwerpen zijn
                  gestructureerd volgens de vragenlijst in CompetenSYS. Het is mogelijk om in
                  onderstaande tekst tussentijds wijzigingen aan te brengen. Controleer de tekst
                  nauwkeurig. Indien akkoord, verzend u de uitwerking naar CompetenSYS.
                </p>
                <p className="text-base sm:text-lg text-brand-red leading-relaxed mt-4">
                  Dit kan even duren. Je kunt dit scherm verlaten. We zullen u per mail
                  informeren wanneer de verwerking is voltooid.
                </p>
              </div>
            </section>

            <figure className="hidden lg:flex flex-1 lg:basis-[45%] items-center justify-end" aria-hidden="true">
              <img
                src={`${process.env.PUBLIC_URL}/assets/man-book.svg`}
                alt=""
                className="w-auto"
                loading="lazy"
              />
            </figure>
          </div>
        )}
      </PageCard>

      {showSaveSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-[26px] bg-brand-blauw text-brand-beige px-10 py-8 max-w-md text-center shadow-xl relative">
            <button
              type="button"
              onClick={() => setShowSaveSuccess(false)}
              className="absolute top-4 right-4"
              aria-label="Sluiten"
            >
              <img src={`${process.env.PUBLIC_URL}/assets/btn-close.svg`} alt="" className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-semibold mb-2">Opgeslagen!</h2>
            <p className="text-base">De samenvatting is succesvol opgeslagen.</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const SummaryPage: React.FC = () => (
  <ErrorBoundary>
    <SummaryPageContent />
  </ErrorBoundary>
);

export default SummaryPage;
